import React from 'react';
import './App.css';
import LegalPage from './LegalPage';
import stormiumTechLogo from './stormium-tech-logo.png';

const legalDocuments = {
  '/privacy': { slug: 'privacy', title: 'Privacy Policy' },
  '/terms-of-service': { slug: 'terms-of-service', title: 'Terms of Service' }
};

const buildCategories = [
  {
    number: '01',
    title: 'SaaS Products',
    description:
      'Cloud-based software and platforms designed to solve focused business and operational problems.'
  },
  {
    number: '02',
    title: 'AI Software',
    description:
      'Software that uses artificial intelligence to support automation, analysis, decision-making, and autonomous workflows.'
  },
  {
    number: '03',
    title: 'Technology Platforms',
    description:
      'Infrastructure and developer-focused systems that enable new ways for software, organizations, and intelligent agents to interact.'
  }
];

const approachThemes = [
  'Practical software',
  'Reliable systems',
  'Thoughtful use of AI',
  'Long-term maintainability'
];

function HomePage() {
  return (
    <div className="site-shell" id="top">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      <header className="site-header">
        <a className="brand" href="#top" aria-label="Stormium Tech home">
          <img className="brand-logo" src={stormiumTechLogo} alt="Stormium Tech logo" />
          <span>Stormium Tech</span>
        </a>

        <nav className="primary-nav" aria-label="Primary navigation">
          <a href="#about">About</a>
          <a href="#contact">Contact</a>
        </nav>
      </header>

      <main id="main-content">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-grid" aria-hidden="true" />
          <div className="hero-content page-width">
            <p className="eyebrow">SaaS and AI software</p>
            <h1 id="hero-title">SaaS and AI software.</h1>
            <p className="hero-lede">
              Stormium Tech is a technology company focused on developing modern Software as a
              Service (SaaS) and artificial intelligence (AI) products.
            </p>
            <p className="hero-supporting">
              We build software designed around practical problems, reliable systems, and emerging
              technologies.
            </p>
            <div className="hero-actions">
              <a className="button button-quiet" href="#about">
                About Stormium
              </a>
            </div>
          </div>
        </section>

        <section className="section section-light" aria-labelledby="build-title">
          <div className="page-width">
            <div className="section-intro">
              <p className="eyebrow eyebrow-dark">Focus areas</p>
              <h2 id="build-title">What We Build</h2>
              <p>
                Stormium Tech develops software products and platforms across SaaS, artificial
                intelligence, automation, and digital infrastructure.
              </p>
            </div>

            <div className="category-grid">
              {buildCategories.map((category) => (
                <article className="category-card" key={category.title}>
                  <span className="card-number">{category.number}</span>
                  <h3>{category.title}</h3>
                  <p>{category.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-ink" id="about" aria-labelledby="approach-title">
          <div className="page-width approach-layout">
            <div className="section-intro section-intro-inverse">
              <p className="eyebrow">Product philosophy</p>
              <h2 id="approach-title">Our Approach</h2>
              <p>
                We focus on building useful technology with strong foundations, clear purpose, and
                long-term maintainability.
              </p>
            </div>

            <div className="approach-panel">
              <p>
                Our work spans software architecture, distributed systems, artificial intelligence,
                automation, and emerging technologies.
              </p>
              <ul>
                {approachThemes.map((theme) => (
                  <li key={theme}>{theme}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="section section-muted" aria-labelledby="about-title">
          <div className="page-width about-layout">
            <div className="section-intro">
              <p className="eyebrow eyebrow-dark">Company</p>
              <h2 id="about-title">About Stormium Tech</h2>
            </div>
            <div className="about-copy">
              <div className="about-block">
                <h3>Why we build</h3>
                <p>
                  We believe good software should make complex work simpler, give people more
                  agency, and turn promising ideas into useful tools. That belief brings us to
                  hard problems and emerging technologies.
                </p>
              </div>
              <div className="about-block">
                <h3>How we work</h3>
                <p>
                  Our culture is curious, direct, and craft-driven. We favor clear thinking over
                  noise, steady progress over hype, and systems that are understandable,
                  dependable, and built to last.
                </p>
              </div>
              <div className="about-block">
                <h3>What we want to accomplish</h3>
                <p>
                  We are building SaaS products, AI software, and technology platforms that are
                  genuinely useful in the real world. We want to make software people can trust,
                  adopt, and grow with.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="contact-section" id="contact" aria-labelledby="contact-title">
          <div className="page-width contact-layout">
            <div>
              <p className="eyebrow">Get in touch</p>
              <h2 id="contact-title">Contact</h2>
            </div>
            <div className="contact-copy">
              <p>For general inquiries, contact Stormium Tech.</p>
              <a className="contact-email" href="mailto:hello@stormiumtech.com">
                hello@stormiumtech.com
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <hr className="footer-divider" aria-hidden="true" />
        <div className="page-width footer-layout">
          <p>© 2026 Stormium Tech LLC. All rights reserved.</p>
          <nav aria-label="Footer navigation">
            <a href="#top">Home</a>
            <a href="#contact">Contact</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms-of-service">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function App() {
  const pathname = window.location.pathname.replace(/\/$/, '') || '/';
  const legalDocument = legalDocuments[pathname as keyof typeof legalDocuments];

  if (legalDocument) {
    return <LegalPage document={legalDocument} />;
  }

  return <HomePage />;
}

export default App;
